import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { Link } from 'react-router-dom';
import { Calendar as CalendarIcon, ArrowLeft, ZoomIn, ZoomOut, CalendarDays, ArrowUpDown, Check, X, Maximize2, FileText, User, CheckCircle2, Clock, ClipboardList, Link2, AlertCircle } from 'lucide-react';

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
};

const colorStyles = {
        green: {
            bg: 'rgba(34, 197, 94, 0.2)',
            border: 'rgba(34, 197, 94, 0.5)',
            text: 'text-green-800 dark:text-green-100',
            badgeBg: 'bg-green-500/10',
            badgeBorder: 'border-green-500/20',
            fill: 'bg-green-500/30',
            labelKey: 'gantt.status_on_time'
        },
        yellow: {
            bg: 'rgba(234, 179, 8, 0.2)',
            border: 'rgba(234, 179, 8, 0.5)',
            text: 'text-yellow-800 dark:text-yellow-100',
            badgeBg: 'bg-yellow-500/10',
            badgeBorder: 'border-yellow-500/20',
            fill: 'bg-yellow-500/30',
            labelKey: 'gantt.status_lagging'
        },
        red: {
            bg: 'rgba(239, 68, 68, 0.2)',
            border: 'rgba(239, 68, 68, 0.5)',
            text: 'text-red-800 dark:text-red-100',
            badgeBg: 'bg-red-500/10',
            badgeBorder: 'border-red-500/20',
            fill: 'bg-red-500/30',
            labelKey: 'gantt.status_overdue'
        }
    };

const getHealth = (start_date, end_date, progress) => {
        if (progress >= 100) return 'green';
        const start = new Date(start_date);
        const end = new Date(end_date);
        const today = new Date();
        start.setHours(0,0,0,0);
        end.setHours(0,0,0,0);
        today.setHours(0,0,0,0);

        if (today > end) return 'red';
        if (today < start) return 'green';

        const totalDays = (end - start) / (1000 * 60 * 60 * 24) || 1;
        const daysPassed = (today - start) / (1000 * 60 * 60 * 24);
        const expectedProgress = (daysPassed / totalDays) * 100;

        if (progress < expectedProgress - 15) return 'yellow';
        return 'green';
    };

const getProjectHealth = (proj) => {
        let totalProgress = 0;
        if (proj.tasks && proj.tasks.length > 0) {
            totalProgress = proj.tasks.reduce((sum, t) => sum + (t.progress || 0), 0) / proj.tasks.length;
        }
        return getHealth(proj.start_date, proj.end_date, totalProgress);
    };

const getProjectProgress = (proj) => {
        if (!proj.tasks || proj.tasks.length === 0) return 0;
        return proj.tasks.reduce((sum, t) => sum + (t.progress || 0), 0) / proj.tasks.length;
    };

function ProjectGanttCard({ project, days, monthHeaders, yearHeaders, t, i18n, getColStart, getColSpan, todayCol, todayStr, sidebarWidth, setSidebarWidth }) {
    const [isResizingSidebar, setIsResizingSidebar] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(2);
    const [viewMode, setViewMode] = useState('monthly');
    const [sortMode, setSortMode] = useState('category');
    const [tooltip, setTooltip] = useState(null);
    const [selectedTaskId, setSelectedTaskId] = useState(null);
    const [selectedTaskDeps, setSelectedTaskDeps] = useState([]);
    const [positions, setPositions] = useState({});
    
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    
    const prevZoomRef = useRef(zoomLevel);
    const zoomOriginRef = useRef(null);
    const hoverTimerRef = useRef(null);

    useLayoutEffect(() => {
        if (prevZoomRef.current !== zoomLevel) {
            const container = scrollContainerRef.current;
            if (container && prevZoomRef.current > 0) {
                const ratio = zoomLevel / prevZoomRef.current;
                const originX = zoomOriginRef.current !== null ? zoomOriginRef.current : (container.clientWidth / 2);
                
                const lookAtX = container.scrollLeft + originX;
                const chartX = lookAtX - sidebarWidth;
                const newChartX = chartX * ratio;
                const newLookAtX = sidebarWidth + newChartX;
                
                container.scrollLeft = newLookAtX - originX;
                zoomOriginRef.current = null;
            }
            prevZoomRef.current = zoomLevel;
        }
    }, [zoomLevel]);

    const scrollContainerRef = useRef(null);

    useEffect(() => {
        if (!isResizingSidebar) return;
        const handleGlobalMouseUp = () => setIsResizingSidebar(false);
        const handleGlobalMouseMove = (e) => {
            e.preventDefault();
            const container = scrollContainerRef.current;
            if (!container) return;
            const rect = container.getBoundingClientRect();
            // Since the sidebar is sticky left-0, its visible width is exactly e.clientX - rect.left
            const newWidth = Math.max(200, Math.min(e.clientX - rect.left, 800));
            setSidebarWidth(newWidth);
        };
        window.addEventListener('mouseup', handleGlobalMouseUp);
        window.addEventListener('mousemove', handleGlobalMouseMove);
        return () => {
            window.removeEventListener('mouseup', handleGlobalMouseUp);
            window.removeEventListener('mousemove', handleGlobalMouseMove);
        };
    }, [isResizingSidebar, setSidebarWidth]);


    useEffect(() => {
        if (!isDragging) return;
        const handleGlobalMouseUp = () => setIsDragging(false);
        const handleGlobalMouseMove = (e) => {
            e.preventDefault();
            const walk = (e.clientX - startX) * 2;
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollLeft = scrollLeft - walk;
            }
        };
        window.addEventListener('mouseup', handleGlobalMouseUp);
        window.addEventListener('mousemove', handleGlobalMouseMove);
        return () => {
            window.removeEventListener('mouseup', handleGlobalMouseUp);
            window.removeEventListener('mousemove', handleGlobalMouseMove);
        };
    }, [isDragging, startX, scrollLeft]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || !selectedTaskId) {
            setPositions({});
            return;
        }

        const updatePositions = () => {
            const taskRows = container.querySelectorAll('[data-task-id]');
            if (taskRows.length === 0) return;

            const containerRect = container.getBoundingClientRect();
            const sL = container.scrollLeft;
            const sT = container.scrollTop;
            const newPositions = {};

            taskRows.forEach(row => {
                const tid = row.getAttribute('data-task-id');
                const barEl = row.querySelector(`[data-bar-id="${tid}"]`);
                if (!barEl) return;
                const barRect = barEl.getBoundingClientRect();
                const rowRect = row.getBoundingClientRect();
                newPositions[tid] = {
                    y: rowRect.top - containerRect.top + sT + rowRect.height / 2,
                    xL: barRect.left - containerRect.left + sL,
                    xR: barRect.right - containerRect.left + sL,
                };
            });
            setPositions(newPositions);
        };

        // Run immediately after render/update
        updatePositions();
        
        // Also run in next frame to ensure browser layout is stable
        const rafId = requestAnimationFrame(updatePositions);

        // Add event listeners for dynamic scroll/resize updates
        container.addEventListener('scroll', updatePositions);
        window.addEventListener('resize', updatePositions);

        return () => {
            cancelAnimationFrame(rafId);
            container.removeEventListener('scroll', updatePositions);
            window.removeEventListener('resize', updatePositions);
        };
    }, [selectedTaskId, selectedTaskDeps, zoomLevel, viewMode, sidebarWidth, project.tasks]);

    const getMinZoom = () => {
        const container = scrollContainerRef.current;
        if (!container || !days || days.length === 0) return 1;
        const availableWidth = container.clientWidth - sidebarWidth - 20;
        return availableWidth > 0 ? availableWidth / days.length : 1;
    };

    const handleZoomIn = () => setZoomLevel(z => Math.min(z * 1.25, 80));
    const handleZoomOut = () => setZoomLevel(z => Math.max(z * 0.8, getMinZoom()));

    const handleFitToScreen = () => {
        const container = scrollContainerRef.current;
        if (!container || !days || days.length === 0) return;
        const availableWidth = container.clientWidth - sidebarWidth - 20;
        const optimalZoom = Math.max(getMinZoom(), Math.min(80, availableWidth / days.length));
        setZoomLevel(optimalZoom);
        setTimeout(() => {
            container.scrollTo({ left: 0, behavior: 'smooth' });
        }, 50);
    };

    const setDailyView = () => { setViewMode('daily'); setZoomLevel(40); setTimeout(() => centerOnToday(40), 50); };
    const setMonthlyView = () => { 
        setViewMode('monthly'); 
        const minZ = getMinZoom();
        setZoomLevel(minZ); 
        setTimeout(() => centerOnToday(minZ), 50); 
    };

    useEffect(() => {
        if (zoomLevel < 20 && viewMode === 'daily') setViewMode('monthly');
        else if (zoomLevel >= 20 && viewMode === 'monthly') setViewMode('daily');
    }, [zoomLevel, viewMode]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || !days || days.length === 0) return;

        const enforceMinZoom = () => {
            const minZ = getMinZoom();
            if (zoomLevel < minZ) {
                setZoomLevel(minZ);
            }
        };

        enforceMinZoom();

        const resizeObserver = new ResizeObserver(() => {
            enforceMinZoom();
        });
        resizeObserver.observe(container);

        return () => {
            resizeObserver.disconnect();
        };
    }, [days, sidebarWidth, zoomLevel]);

    const centerOnToday = (targetZoom) => {
        const container = scrollContainerRef.current;
        if (!container || !days || days.length === 0) return;
        const idx = days.findIndex(d => d.toISOString().split('T')[0] === todayStr);
        if (idx === -1) return;
        const todayX = sidebarWidth + idx * targetZoom;
        container.scrollTo({ left: Math.max(0, todayX - container.clientWidth / 2), behavior: 'smooth' });
    };

    useEffect(() => {
        if (days.length > 0) {
            const container = scrollContainerRef.current;
            if (container) {
                // Auto-fit: calculate zoom so chart fills available width
                const availableWidth = container.clientWidth - sidebarWidth - 20;
                const fitZoom = availableWidth / days.length;
                // Clamp between 1 and 80, use fit zoom if it's larger than default
                const optimalZoom = Math.max(1, Math.min(80, fitZoom));
                if (optimalZoom > zoomLevel) {
                    setZoomLevel(optimalZoom);
                    if (optimalZoom >= 20) setViewMode('daily');
                    setTimeout(() => centerOnToday(optimalZoom), 100);
                } else {
                    setTimeout(() => centerOnToday(zoomLevel), 100);
                }
            } else {
                setTimeout(() => centerOnToday(zoomLevel), 100);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days.length]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleWheel = (e) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const rect = container.getBoundingClientRect();
                zoomOriginRef.current = e.clientX - rect.left;

                setZoomLevel(z => {
                    if (e.deltaY > 0) return Math.max(z * 0.8, 1);
                    return Math.min(z * 1.25, 80);
                });
            }
        };

        container.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, []);

    return (
        <div className="flex flex-col bg-surface rounded-2xl border border-border shadow-sm overflow-hidden relative flex-1">
            {/* Card Header */}
            <div className="flex items-center justify-between p-4 border-b border-border/50 bg-surface-2/30">
                <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: project.color || 'var(--brand)' }} />
                    <h2 className="font-bold text-lg text-primary">{project.name}</h2>
                </div>
                
                {/* Local View Controls */}
                <div className="flex items-center gap-2 bg-surface p-1 rounded-lg border border-border shadow-sm">
                    <button onClick={setDailyView} className={`px-2 py-1 text-xs font-medium rounded-md transition-all ${viewMode === 'daily' ? 'bg-brand text-white shadow' : 'text-muted hover:text-primary hover:bg-surface-2'}`}>{t('gantt.view_daily')}</button>
                    <button onClick={setMonthlyView} className={`px-2 py-1 text-xs font-medium rounded-md transition-all ${viewMode === 'monthly' ? 'bg-brand text-white shadow' : 'text-muted hover:text-primary hover:bg-surface-2'}`}>{t('gantt.view_monthly')}</button>
                    <div className="w-px h-4 bg-border mx-1" />
                    <button onClick={handleZoomOut} disabled={zoomLevel <= getMinZoom()} className="p-1 text-muted hover:text-primary hover:bg-surface disabled:opacity-50 rounded-md transition-colors" title={t('gantt.zoom_out')}><ZoomOut className="w-3.5 h-3.5" /></button>
                    <button onClick={handleZoomIn} disabled={zoomLevel >= 80} className="p-1 text-muted hover:text-primary hover:bg-surface disabled:opacity-50 rounded-md transition-colors" title={t('gantt.zoom_in')}><ZoomIn className="w-3.5 h-3.5" /></button>
                    <button onClick={handleFitToScreen} className="p-1 text-muted hover:text-primary hover:bg-surface rounded-md transition-colors" title={t('gantt.fit_screen', 'По экрану')}><Maximize2 className="w-3.5 h-3.5" /></button>
                </div>
            </div>

            {/* Card Body */}
            <div className="flex flex-1 min-h-0 relative overflow-hidden">
                <div 
                    className="overflow-auto flex-1 relative custom-scrollbar select-none" 
                    ref={scrollContainerRef}
                    onMouseDown={(e) => {
                        setIsDragging(true);
                        setStartX(e.clientX);
                        setScrollLeft(scrollContainerRef.current.scrollLeft);
                    }}
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                >
                    <div className="flex flex-col relative" style={{ width: `${sidebarWidth + days.length * zoomLevel}px`, minWidth: '100%' }}>
                        
                        {/* Background Grid */}
                        <div className="absolute top-12 bottom-0 right-0 flex pointer-events-none z-0" style={{ left: `${sidebarWidth}px` }}>
                            {viewMode === 'daily' ? (
                                days.map((d, i) => (
                                    <div key={i} className="flex-shrink-0 border-r border-border/30 h-full" style={{ width: `${zoomLevel}px` }} />
                                ))
                            ) : (
                                monthHeaders.map((m, i) => (
                                    <div key={i} className="flex-shrink-0 border-r border-border/30 h-full" style={{ width: `${m.colSpan * zoomLevel}px` }} />
                                ))
                            )}
                        </div>

                        {/* Today Line */}
                        {todayCol > 1 && (
                            <div className="absolute top-0 bottom-0 border-l border-dashed border-brand/40 z-10 pointer-events-none" style={{ left: `${sidebarWidth + (todayCol - 2) * zoomLevel}px` }}>
                                <div className="absolute top-12 -left-[14px] bg-brand/20 text-brand/80 border border-brand/30 text-[9px] font-semibold px-1.5 py-0.5 rounded shadow-sm backdrop-blur-[2px]">
                                    {t('gantt.today')}
                                </div>
                            </div>
                        )}

                        {/* Header Row */}
                        <div className="flex border-b border-border sticky top-0 z-40 shadow-sm h-12 bg-surface">
                            <div className="sticky left-0 z-40 border-r border-border px-3 font-bold text-xs uppercase flex items-center justify-between flex-shrink-0 text-primary bg-surface transition-[width] duration-200" style={{ width: `${sidebarWidth}px` }}>
                                <span className="text-muted">{t('gantt.tasks', 'Vazifalar')}</span>
                                <button 
                                    onClick={() => setSortMode(s => s === 'category' ? 'chronological' : 'category')} 
                                    className={`p-1 rounded transition-all ${sortMode === 'chronological' ? 'bg-brand text-white shadow-sm' : 'text-muted hover:text-primary hover:bg-surface-2'}`} 
                                    title={sortMode === 'category' ? t('gantt.sort_by_date', 'Sana bo\'yicha saralash') : t('gantt.sort_by_category', 'Kategoriya bo\'yicha saralash')}
                                >
                                    <ArrowUpDown className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            <div className="flex flex-col flex-1 relative">
                                <div className="flex border-b border-border/50 h-6">
                                    {viewMode === 'daily' ? (
                                        monthHeaders.map((m, i) => (
                                            <div key={`top-m-${i}`} className="flex items-center justify-center border-r border-border/30 px-2 flex-shrink-0 overflow-hidden" style={{ width: `${m.colSpan * zoomLevel}px` }}>
                                                <span className="text-[10px] font-bold text-primary capitalize sticky" style={{ left: `${sidebarWidth + 10}px` }}>
                                                    {new Date(m.year, m.month, 1).toLocaleString(i18n.language || 'ru', { month: 'long', year: 'numeric' })}
                                                </span>
                                            </div>
                                        ))
                                    ) : (
                                        yearHeaders.map((y, i) => (
                                            <div key={`top-y-${i}`} className="flex items-center justify-center border-r border-border/30 px-2 flex-shrink-0 overflow-hidden" style={{ width: `${y.colSpan * zoomLevel}px` }}>
                                                <span className="text-[10px] font-bold text-primary sticky" style={{ left: `${sidebarWidth + 10}px` }}>
                                                    {y.year}
                                                </span>
                                            </div>
                                        ))
                                    )}
                                </div>
                                <div className="flex h-6">
                                    {viewMode === 'daily' ? (
                                        days.map((d, i) => {
                                            const isToday = d.toISOString().split('T')[0] === todayStr;
                                            return (
                                                <div key={i} className={`flex-shrink-0 border-r border-border/30 flex items-center justify-center ${isToday ? 'bg-brand/10' : ''}`} style={{ width: `${zoomLevel}px` }}>
                                                    <span className={`text-[10px] font-semibold ${isToday ? 'text-brand' : 'text-primary'}`}>{d.getDate()}</span>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        monthHeaders.map((m, i) => (
                                            <div key={i} className="flex items-center justify-center border-r border-border/30 flex-shrink-0" style={{ width: `${m.colSpan * zoomLevel}px` }}>
                                                <span className="text-[10px] font-semibold text-primary capitalize">
                                                    {new Date(m.year, m.month, 1).toLocaleString(i18n.language || 'ru', { month: 'short' })}
                                                </span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Tasks Rows */}
                        <div className="flex flex-col relative z-20">
                            {(() => {
                                const renderTaskRow = (task, rowIndex) => {
                                    const isSelected = selectedTaskId === task.id;
                                    const isDependency = selectedTaskDeps.includes(task.id);
                                    return (
                                    <div key={task.id} data-task-id={task.id} className={`flex min-h-[36px] py-1 border-b border-border/20 transition-all relative group/task hover:bg-surface-2/20 ${isSelected ? 'bg-brand/5' : ''} ${isDependency ? 'bg-amber-500/5' : ''}`}>
                                        <div className="sticky left-0 z-30 backdrop-blur-sm border-r border-border px-4 flex items-center justify-between flex-shrink-0 overflow-hidden group/sidebar transition-[width] duration-200" style={{ width: `${sidebarWidth}px`, backgroundColor: project.color ? `${project.color}15` : 'var(--surface)' }}>
                                            <div 
                                                className="flex items-start gap-2 overflow-hidden flex-1 relative z-10 cursor-pointer hover:opacity-80 transition-opacity"
                                                onClick={() => {
                                                    const requiredWidth = Math.min(800, Math.max(300, task.name.length * 7 + 160));
                                                    setSidebarWidth(sidebarWidth === requiredWidth ? 300 : requiredWidth);
                                                }}
                                                onMouseEnter={(e) => {
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    hoverTimerRef.current = setTimeout(() => {
                                                        setTooltip({ task, x: rect.left + rect.width / 2, y: rect.top });
                                                    }, 400);
                                                }}
                                                onMouseLeave={() => {
                                                    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                                                    setTooltip(null);
                                                }}
                                            >
                                                <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${colorStyles[getHealth(task.start_date, task.end_date, task.progress)].badgeBg.replace('/10', '/50')} border ${colorStyles[getHealth(task.start_date, task.end_date, task.progress)].badgeBorder}`} />
                                                <div className="flex flex-col flex-1 min-w-0">
                                                    <span className="font-medium text-xs text-primary truncate" title={task.name}>{task.name}</span>
                                                    {task.notes && (
                                                        <span className="text-[10px] text-muted-foreground/70 dark:text-muted/50 truncate" title={task.notes}>{task.notes}</span>
                                                    )}
                                                </div>
                                            </div>
    <div 
        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-brand/50 active:bg-brand z-50 flex items-center justify-center opacity-0 group-hover/sidebar:opacity-100 transition-opacity"
        onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsResizingSidebar(true);
        }}
    >
        <div className="w-0.5 h-6 bg-brand rounded-full" />
    </div>

                                            <div className="flex items-center gap-3 flex-shrink-0 ml-2 relative z-10">
                                                <span className="text-[10px] text-muted truncate max-w-[80px]" title={task.assignee}>{task.assignee}</span>
                                            </div>
                                        </div>
                                        <div className="relative flex-1 pointer-events-none">
                                            {(() => {
                                                const taskWidth = getColSpan(task.start_date, task.end_date) * zoomLevel;
                                                const displayWidth = Math.max(taskWidth, 4);
                                                const isSmall = displayWidth < 30;
                                                const health = getHealth(task.start_date, task.end_date, task.progress);
                                                return (
                                                    <div 
                                                        data-bar-id={task.id}
                                                        className={`absolute top-1/2 -translate-y-1/2 h-4 rounded shadow-sm group-hover/task:shadow transition-all z-20 flex items-center pointer-events-auto cursor-pointer ${isSmall ? '' : 'overflow-hidden'}`}
                                                        style={{ 
                                                            left: `${(getColStart(task.start_date) - 2) * zoomLevel}px`,
                                                            width: `${displayWidth}px`,
                                                            backgroundColor: isSmall ? colorStyles[health].border : colorStyles[health].bg,
                                                            borderColor: isSelected ? 'var(--brand)' : isDependency ? 'rgb(245, 158, 11)' : colorStyles[health].border,
                                                            borderWidth: isSelected ? '2px' : isDependency ? '2px' : '1px',
                                                            boxShadow: isSelected ? '0 0 8px var(--brand)' : isDependency ? '0 0 6px rgba(245, 158, 11, 0.4)' : 'none'
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (selectedTaskId === task.id) {
                                                                setSelectedTaskId(null);
                                                                setSelectedTaskDeps([]);
                                                            } else {
                                                                setSelectedTaskId(task.id);
                                                                setSelectedTaskDeps(task.dependency_ids || []);
                                                            }
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            setTooltip({ task, x: rect.left + rect.width / 2, y: rect.top });
                                                        }}
                                                        onMouseLeave={() => setTooltip(null)}
                                                    >
                                                        <div 
                                                            className={`absolute left-0 top-0 bottom-0 ${colorStyles[health].fill} ${isSmall ? 'hidden' : ''}`}
                                                            style={{ width: `${task.progress}%` }}
                                                        />
                                                        {!isSmall && (
                                                            <span className={`relative z-10 text-[9px] font-bold px-1.5 flex items-center gap-1 overflow-hidden drop-shadow-sm ${colorStyles[health].text}`}>
                                                                {task.progress >= 100 ? <Check size={12} strokeWidth={3} className="flex-shrink-0" /> :
                                                                 health === 'red' ? <X size={11} strokeWidth={3} className="flex-shrink-0 text-red-600 dark:text-red-400" /> :
                                                                 health === 'yellow' ? <span className="flex-shrink-0 font-extrabold text-[12px] leading-none px-[2px] text-yellow-600 dark:text-yellow-400">!</span> : null}
                                                                <span className="truncate leading-none pt-[1px]">{task.name}</span>
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                            </div>
                                        </div>
                                    );
                                };

                                const uncatTasks = project.tasks?.filter(t => !t.section_id) || [];

                                if (sortMode === 'chronological') {
                                    const allTasks = [...(project.tasks || [])].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
                                    return <>{allTasks.map(renderTaskRow)}</>;
                                }
                                
                                return (
                                    <>
                                        {uncatTasks.map(renderTaskRow)}
                                        {project.sections?.map(section => {
                                            const sectionTasks = project.tasks?.filter(t => t.section_id === section.id) || [];
                                            if (sectionTasks.length === 0) return null;
                                            return (
                                                <div key={section.id} className="flex flex-col">
                                                    <div className="flex h-8 border-b border-border/50 bg-surface-2/80 relative">
                                                        <div className="sticky left-0 z-30 px-4 flex items-center border-r border-border group/sidebar transition-[width] duration-200" style={{ width: `${sidebarWidth}px`, backgroundColor: project.color ? `${project.color}08` : 'var(--surface-2)' }}>
                                                            <span className="font-bold text-xs text-primary truncate uppercase tracking-wider">{section.name}</span>
                                                        
    <div 
        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-brand/50 active:bg-brand z-50 flex items-center justify-center opacity-0 group-hover/sidebar:opacity-100 transition-opacity"
        onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsResizingSidebar(true);
        }}
    >
        <div className="w-0.5 h-6 bg-brand rounded-full" />
    </div>

                                                        </div>
                                                        <div className="flex-1" />
                                                    </div>
                                                    {sectionTasks.map(renderTaskRow)}
                                                </div>
                                            );
                                        })}
                                    </>
                                );
                            })()}
                        </div>
                    </div>

                    {selectedTaskId && (() => {
                        const container = scrollContainerRef.current;
                        if (!container) return null;
                        const sel = positions[selectedTaskId];
                        if (!sel) return null;
                        
                        // Find predecessors (tasks selected task depends on)
                        const selTask = project.tasks?.find(t => t.id === selectedTaskId);
                        const selDeps = selTask?.dependency_ids || [];
                        
                        // Find successors (tasks that depend on selected task)
                        const selSuccessors = project.tasks?.filter(t => t.dependency_ids && t.dependency_ids.includes(selectedTaskId)) || [];
                        
                        if (selDeps.length === 0 && selSuccessors.length === 0) return null;
                        
                        const ho = 48;
                        const svgW = sidebarWidth + days.length * zoomLevel;
                        const svgH = container.scrollHeight;
                        const R = 6; // corner radius
                        const PAD = 8; // padding from task edge
                        
                        return (
                            <svg
                                className="absolute top-12 left-0 pointer-events-none"
                                width={svgW} height={svgH - ho}
                                style={{ overflow: 'visible', zIndex: 15 }}
                            >
                                <defs>
                                    <marker id="dep-arr" viewBox="0 0 10 10" refX="9" refY="5"
                                        markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                                        <path d="M 0 1 L 9 5 L 0 9 Z" fill="#f59e0b" />
                                    </marker>
                                    <marker id="dep-arr-blue" viewBox="0 0 10 10" refX="9" refY="5"
                                        markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                                        <path d="M 0 1 L 9 5 L 0 9 Z" fill="#00d2ff" />
                                    </marker>
                                </defs>
                                
                                {/* 1. Render Predecessor Arrows (Amber) */}
                                {selDeps.map((depId, idx) => {
                                    const dep = positions[depId];
                                    if (!dep) return null;

                                    const fromX = dep.xR + 2;
                                    const fromY = dep.y - ho;
                                    const toX = sel.xL - 2;
                                    const toY = sel.y - ho;

                                    const r = R;
                                    const stagger = idx * 4;
                                    const dy = toY - fromY;
                                    const dirY = dy > 0 ? 1 : -1;

                                    let d;
                                    if (fromX + 12 < toX) {
                                        const midX = fromX + (toX - fromX) / 2 + stagger;
                                        d = [
                                            `M ${fromX} ${fromY}`,
                                            `L ${midX - r} ${fromY}`,
                                            `Q ${midX} ${fromY}, ${midX} ${fromY + r * dirY}`,
                                            `L ${midX} ${toY - r * dirY}`,
                                            `Q ${midX} ${toY}, ${midX + r} ${toY}`,
                                            `L ${toX} ${toY}`
                                        ].join(' ');
                                    } else {
                                        const rightX = fromX + PAD + stagger;
                                        const leftX = toX - PAD - stagger;
                                        const midY = fromY + dy / 2;

                                        d = [
                                            `M ${fromX} ${fromY}`,
                                            `L ${rightX - r} ${fromY}`,
                                            `Q ${rightX} ${fromY}, ${rightX} ${fromY + r * dirY}`,
                                            `L ${rightX} ${midY - r * dirY}`,
                                            `Q ${rightX} ${midY}, ${rightX - r} ${midY}`,
                                            `L ${leftX + r} ${midY}`,
                                            `Q ${leftX} ${midY}, ${leftX} ${midY + r * dirY}`,
                                            `L ${leftX} ${toY - r * dirY}`,
                                            `Q ${leftX} ${toY}, ${leftX + r} ${toY}`,
                                            `L ${toX} ${toY}`
                                        ].join(' ');
                                    }

                                    return (
                                        <g key={`pred-${depId}`}>
                                            <path d={d} stroke="rgba(245,158,11,0.06)" strokeWidth="8" fill="none" strokeLinecap="round" />
                                            <path d={d} stroke="#f59e0b" strokeWidth="1.5" fill="none"
                                                strokeLinecap="round" strokeLinejoin="round"
                                                markerStart="url(#dep-arr)" opacity="0.85" />
                                            <circle cx={toX} cy={toY} r="3" fill="#f59e0b" opacity="0.5" />
                                        </g>
                                    );
                                })}

                                {/* 2. Render Successor Arrows (Blue) */}
                                {selSuccessors.map((succTask, idx) => {
                                    const succ = positions[succTask.id];
                                    if (!succ) return null;

                                    const fromX = sel.xR + 2;
                                    const fromY = sel.y - ho;
                                    const toX = succ.xL - 2;
                                    const toY = succ.y - ho;

                                    const r = R;
                                    const stagger = idx * 4;
                                    const dy = toY - fromY;
                                    const dirY = dy > 0 ? 1 : -1;

                                    let d;
                                    if (fromX + 12 < toX) {
                                        const midX = fromX + (toX - fromX) / 2 + stagger;
                                        d = [
                                            `M ${fromX} ${fromY}`,
                                            `L ${midX - r} ${fromY}`,
                                            `Q ${midX} ${fromY}, ${midX} ${fromY + r * dirY}`,
                                            `L ${midX} ${toY - r * dirY}`,
                                            `Q ${midX} ${toY}, ${midX + r} ${toY}`,
                                            `L ${toX} ${toY}`
                                        ].join(' ');
                                    } else {
                                        const rightX = fromX + PAD + stagger;
                                        const leftX = toX - PAD - stagger;
                                        const midY = fromY + dy / 2;

                                        d = [
                                            `M ${fromX} ${fromY}`,
                                            `L ${rightX - r} ${fromY}`,
                                            `Q ${rightX} ${fromY}, ${rightX} ${fromY + r * dirY}`,
                                            `L ${rightX} ${midY - r * dirY}`,
                                            `Q ${rightX} ${midY}, ${rightX - r} ${midY}`,
                                            `L ${leftX + r} ${midY}`,
                                            `Q ${leftX} ${midY}, ${leftX} ${midY + r * dirY}`,
                                            `L ${leftX} ${toY - r * dirY}`,
                                            `Q ${leftX} ${toY}, ${leftX + r} ${toY}`,
                                            `L ${toX} ${toY}`
                                        ].join(' ');
                                    }

                                    return (
                                        <g key={`succ-${succTask.id}`}>
                                            <path d={d} stroke="rgba(0,210,255,0.06)" strokeWidth="8" fill="none" strokeLinecap="round" />
                                            <path d={d} stroke="#00d2ff" strokeWidth="1.5" fill="none"
                                                strokeLinecap="round" strokeLinejoin="round"
                                                markerStart="url(#dep-arr-blue)" opacity="0.85" />
                                            <circle cx={toX} cy={toY} r="3" fill="#00d2ff" opacity="0.5" />
                                        </g>
                                    );
                                })}
                            </svg>
                        );
                    })()}
                </div>
            </div>
            {/* Tooltip Portal */}
            {tooltip && tooltip.type === 'minimal' && createPortal(
                <div
                    className="fixed z-[9999] pointer-events-none bg-surface text-primary text-[11px] font-medium px-2 py-1 rounded shadow-md border border-border"
                    style={{ left: `${tooltip.x + 12}px`, top: `${tooltip.y + 12}px` }}
                >
                    {tooltip.text}
                </div>,
                document.body
            )}
            {tooltip && !tooltip.type && createPortal(
                <div
                    className="fixed z-[9999] pointer-events-none"
                    style={{ 
                        left: `${Math.min(window.innerWidth - 220, Math.max(110, tooltip.x))}px`, 
                        top: `${tooltip.y - 8 < 180 ? tooltip.y + 24 : tooltip.y - 8}px`, 
                        transform: tooltip.y - 8 < 180 ? 'translateX(-50%)' : 'translate(-50%, -100%)' 
                    }}
                >
                    <div className="bg-surface border border-border rounded-lg shadow-xl p-3 max-w-[300px] min-w-[200px] overflow-hidden relative" style={{ backdropFilter: 'blur(8px)' }}>
                        <div className="flex items-start gap-1.5">
                            <span 
                                className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${tooltip.task.is_critical ? 'bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.5)]' : 'bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.5)]'}`} 
                                title={tooltip.task.is_critical ? t('gantt.critical_task', 'Критическая задача') : t('gantt.normal_task', 'Не критическая задача')} 
                            />
                            <p className="font-bold text-sm text-primary leading-tight break-words line-clamp-3 flex-1">{tooltip.task.name}</p>
                        </div>
                        <p className="text-[11px] text-muted mt-1.5">{formatDate(tooltip.task.start_date)} — {formatDate(tooltip.task.end_date)}</p>
                        <div className="flex items-center gap-2 mt-1">
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1 ${
                                tooltip.task.status === 'done' ? 'bg-green-500/10 text-green-600 dark:text-green-400' : 
                                tooltip.task.status === 'in_progress' ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400' : 
                                'bg-surface-2 text-muted border border-border/40'
                            }`}>
                                {tooltip.task.status === 'done' ? (
                                    <>
                                        <CheckCircle2 size={11} className="text-green-600 dark:text-green-400" />
                                        <span>{t('gantt.status_done', 'Done')}</span>
                                    </>
                                ) : tooltip.task.status === 'in_progress' ? (
                                    <>
                                        <Clock size={11} className="text-yellow-600 dark:text-yellow-400" />
                                        <span>{t('gantt.status_in_progress', 'In Progress')}</span>
                                    </>
                                ) : (
                                    <>
                                        <ClipboardList size={11} className="text-muted" />
                                        <span>{t('gantt.status_todo', 'To Do')}</span>
                                    </>
                                )}
                            </span>
                        </div>
                        {tooltip.task.assignee && (
                            <div className="text-[11px] text-brand mt-1.5 pt-1.5 border-t border-border/50 flex items-center gap-1">
                                <User size={12} className="text-brand flex-shrink-0" />
                                <span className="truncate">{tooltip.task.assignee}</span>
                            </div>
                        )}
                        {tooltip.task.dependency_ids && tooltip.task.dependency_ids.length > 0 && (
                            <div className="mt-1.5 pt-1.5 border-t border-border/50">
                                <p className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 mb-0.5 flex items-center gap-1">
                                    <Link2 size={11} className="text-amber-500 flex-shrink-0" />
                                    <span>{t('gantt.dependencies', 'Зависимости')}:</span>
                                </p>
                                {tooltip.task.dependency_ids.map(depId => {
                                    const depTask = project.tasks?.find(t => t.id === depId);
                                    return depTask ? (
                                        <div key={depId} className="text-[10px] text-muted pl-2 flex items-center gap-1 mt-0.5">
                                            {depTask.status === 'done' ? (
                                                <CheckCircle2 size={10} className="text-green-500 flex-shrink-0" />
                                            ) : (
                                                <Clock size={10} className="text-amber-500 flex-shrink-0" />
                                            )}
                                            <span className="truncate">{depTask.name}</span>
                                        </div>
                                    ) : null;
                                })}
                            </div>
                        )}
                        {tooltip.task.notes && (
                            <div className="mt-1.5 pt-1.5 border-t border-border/50 flex items-start gap-1">
                                <FileText size={12} className="text-muted flex-shrink-0 mt-0.5" />
                                <p className="text-[10px] text-muted leading-tight flex-1">
                                    <span className="font-semibold text-primary/70">{t('gantt.note', 'Примечание')}:</span> {tooltip.task.notes}
                                </p>
                            </div>
                        )}
                        {tooltip.y - 8 < 180 ? (
                            <div className="absolute left-1/2 -translate-x-1/2 top-[-5px] w-2.5 h-2.5 rotate-45 bg-surface border-l border-t border-border" />
                        ) : (
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-[-5px] w-2.5 h-2.5 rotate-45 bg-surface border-r border-b border-border" />
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export default function GanttViewerPage({ projectId }) {
    const { t, i18n } = useTranslation();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sidebarWidth, setSidebarWidth] = useState(300);

    useEffect(() => {
        fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const { data } = await api.get('/gantt/projects');
            let filteredProjects = data;
            if (projectId) {
                filteredProjects = data.filter(p => String(p.id) === String(projectId));
            }
            const sortedProjects = filteredProjects.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
            const projectsWithTasks = await Promise.all(
                sortedProjects.map(async (p) => {
                    const [taskRes, sectionRes] = await Promise.all([
                        api.get(`/gantt/projects/${p.id}/tasks`),
                        api.get(`/gantt/projects/${p.id}/sections`)
                    ]);
                    const sortedTasks = taskRes.data.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
                    return { ...p, tasks: sortedTasks, sections: sectionRes.data };
                })
            );
            setProjects(projectsWithTasks);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const { minDate, maxDate, days } = useMemo(() => {
        let min, max;

        if (projects.length > 0) {
            // Initialize with the first project's dates
            min = new Date(projects[0].start_date);
            max = new Date(projects[0].end_date);
            
            projects.forEach(p => {
                const pStart = new Date(p.start_date);
                const pEnd = new Date(p.end_date);
                if (pStart < min) min = new Date(pStart);
                if (pEnd > max) max = new Date(pEnd);
                
                p.tasks?.forEach(t => {
                    const tStart = new Date(t.start_date);
                    const tEnd = new Date(t.end_date);
                    if (tStart < min) min = new Date(tStart);
                    if (tEnd > max) max = new Date(tEnd);
                });
            });
            // Kichik padding (2 oy oldinga va orqaga)
            min.setMonth(min.getMonth() - 2);
            max.setMonth(max.getMonth() + 2);
        } else {
            // Fallback if no projects exist
            const currentYear = new Date().getFullYear();
            min = new Date(currentYear - 1, 0, 1);
            max = new Date(currentYear + 1, 11, 31);
        }

        // Keep it aligned to the first day of min month and last day of max month
        min.setDate(1);
        max.setMonth(max.getMonth() + 1, 0);

        const dArr = [];
        let cur = new Date(min);
        while (cur <= max) {
            dArr.push(new Date(cur));
            cur.setDate(cur.getDate() + 1);
        }

        return { minDate: min, maxDate: max, days: dArr };
    }, [projects]);

    const monthHeaders = useMemo(() => {
        const headers = [];
        if (!days.length) return headers;
        let currentMonth = days[0].getMonth();
        let currentYear = days[0].getFullYear();
        let count = 0;
        let startIdx = 2;
        
        days.forEach((d) => {
            if (d.getMonth() === currentMonth && d.getFullYear() === currentYear) {
                count++;
            } else {
                headers.push({ month: currentMonth, year: currentYear, colSpan: count, colStart: startIdx });
                startIdx += count;
                currentMonth = d.getMonth();
                currentYear = d.getFullYear();
                count = 1;
            }
        });
        if (count > 0) {
            headers.push({ month: currentMonth, year: currentYear, colSpan: count, colStart: startIdx });
        }
        return headers;
    }, [days]);

    const yearHeaders = useMemo(() => {
        const headers = [];
        if (!monthHeaders.length) return headers;
        let currentYear = monthHeaders[0].year;
        let count = 0;
        monthHeaders.forEach(m => {
            if (m.year === currentYear) {
                count += m.colSpan;
            } else {
                headers.push({ year: currentYear, colSpan: count });
                currentYear = m.year;
                count = m.colSpan;
            }
        });
        if (count > 0) {
            headers.push({ year: currentYear, colSpan: count });
        }
        return headers;
    }, [monthHeaders]);

    const getColStart = (dateStr) => {
        const d = new Date(dateStr);
        const idx = days.findIndex(day => day.toISOString().split('T')[0] === d.toISOString().split('T')[0]);
        return idx !== -1 ? idx + 2 : 2;
    };
    
    const getColSpan = (startStr, endStr) => {
        const s = new Date(startStr);
        const e = new Date(endStr);
        const diffTime = Math.abs(e - s);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
        return diffDays + 1;
    };

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-8 h-8 rounded-full border-4 border-brand border-t-transparent animate-spin" />
            </div>
        );
    }

    if (!projects.length) {
        return (
            <div className="flex-1 p-8 text-center text-muted flex flex-col items-center justify-center h-full">
                <CalendarIcon className="w-16 h-16 opacity-20 mb-4" />
                <h2 className="text-xl font-bold">{t('gantt.no_projects', 'Нет проектов')}</h2>
                <p>{t('gantt.no_projects_desc', 'Gantt-диаграммы пока не добавлены.')}</p>
            </div>
        );
    }

    const todayStr = new Date().toISOString().split('T')[0];
    const todayCol = getColStart(todayStr);

    return (
        <div className="flex-1 overflow-auto bg-surface relative h-full flex flex-col p-4 pt-6">
            <div className="flex-1 flex flex-col relative overflow-y-auto pr-2 custom-scrollbar">
                {projects.map(proj => (
                    <ProjectGanttCard 
                        key={proj.id}
                        project={proj}
                        days={days}
                        monthHeaders={monthHeaders}
                        yearHeaders={yearHeaders}
                        t={t}
                        i18n={i18n}
                        getColStart={getColStart}
                        getColSpan={getColSpan}
                        todayCol={todayCol}
                        todayStr={todayStr}
                        sidebarWidth={sidebarWidth}
                        setSidebarWidth={setSidebarWidth}
                    />
                ))}
            </div>
            
            <style jsx>{`
                .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: var(--muted); }
            `}</style>
        </div>
    );
}
